<template>
	<mounting-portal
		v-if="showingMask"
		mountTo="#dialog-portal"
		append
		multiple
	>
		<transition name="fade">
			<div
				v-if="showingMask"
				ref="mask"
				class="timpex-modal-mask"
				@mousedown="onMaskClicked"
				@touchstart="onMaskClicked"
			>
				<transition name="open" @after-enter="onContainerShowing">
					<div
						v-show="showingContainer"
						ref="focusBoundary"
						v-focus-boundary.circular="{ id: `tdialog-${id}`}"
						:style="widthStyle"
						tabindex="0"
						class="timpex-modal-container"
						:class="{ 'large': large }"
						:data-focus-trap="trapFocus"
					>
						<div v-if="hasHeader" class="timpex-modal-header">
							<!-- @slot Slot for header content. Defaults to title -->
							<slot name="header">
								<div class="timpex-modal-header-inner">
									<t-timpex-icon
										v-if="icon"
										:icon="icon"
										:type="iconType"
										:color="iconColor"
										class="timpex-modal-icon"
									/>
									<div class="timpex-modal-title">{{ title }}</div>
									<slot name="title-after" />
								</div>
							</slot>
							<TIconButton
								v-if="showHeaderClose"
								aria-label="Close"
								appearance="transparent"
								type="bp"
								icon="cross"
								@click="onCancelClicked"
							/>
						</div>
						<div
							v-if="hasBody"
							ref="content"
							:style="{
								...bodyStyle,
								padding: paddingModalBody,
							}"
							class="timpex-modal-body"
							:class="{'timpex-body-no-header': !hasHeader }"
						>
							<!-- @slot Content of dialog -->
							<slot class="timpex-modal-content"/>
						</div>
						<div
							v-if="!hasBody"
							class="timpex-modal-body-scroll"
							:class="{'timpex-body-no-header-scroll': !hasHeader }"
						>
							<!-- @slot Content of dialog -->
							<slot/>
						</div>
						<footer v-if="hasFooter" :class="footerClass" class="timpex-modal-footer">
							<div v-if="hasFooter" class="timpex-modal-footer-content">
								<!-- @slot Slot for footer content. Defaults to confirm and cancel buttons-->
								<slot name="footer">
									<div style="display:flex;">
										<slot name="additional-buttons"/>
									</div>
									<div>
										<TButton
											v-if="hasCancel"
											class="timpex-modal-button"
											:busy="busy"
											@click="onCancelClicked"
										>{{ getCancelLabel }}</TButton>
										<TButton
											v-if="hasConfirm"
											:intent="intent"
											:disabled="isConfirmDisabled"
											class="timpex-modal-button"
											appearance="primary"
											:busy="busy"
											@click="onConfirmClicked"
										>{{ getConfirmLabel }}</TButton>

									</div>
								</slot>
							</div>

						</footer>
					</div>
				</transition>
			</div>
		</transition>
	</mounting-portal>
</template>

<script>
import TButton from '../buttons/TButton';
import TIconButton from '../buttons/TIconButton';
import dialogMixin from './dialogMixin';
import { delay, keydownController } from '@/core';
import { v4 as newUUID } from 'uuid';
import dialogStack from './dialogStack';

export default {
	name: 'TDialog',
	components: {
		TButton,
		TIconButton
	},
	mixins: [dialogMixin],
	data() {
		return {
			id: newUUID(),
			showingMask: false,
			showingContainer: false,
			closing: false,
			opening: false,
			stack: dialogStack,
			trapFocus: false,
		};
	},

	computed: {
		widthStyle() {
			if (!this.width) return '';
			return { width: this.width, minWidth: this.width };
		},
		getConfirmLabel() {
			return this.confirmLabel || 'Ok';
		},
		getCancelLabel() {
			return this.cancelLabel || 'Cancel';
		},
	},

	watch: {
		async isShown(newValue, oldValue) {
			if (this.closing || this.opening || newValue === oldValue) return;
			if (newValue && !oldValue) {
				this.onShow();
			} else if (!newValue && oldValue) {
				this.onClose();
			}
		}
	},

	mounted() {
		this.keyListeners.forEach(({ key, listener, modifiers }) => {
			keydownController.addDialogKeyListener(this.id, key, listener, modifiers);
		});
	},
	beforeDestroy() {
		this.keyListeners.forEach(({ key, listener }) => {
			keydownController.removeKeyListener(key, listener);
		});
	},

	methods: {
		async onShow() {
			this.negotiateAddGlobalKeydownEventListener();
			this.opening = true;
			this.showingMask = true;
			await delay(75);
			this.showingContainer = true;

			this.$nextTick(() => {
				this.stack.push({
					id: this.id,
					cancelWithEsc: () => {
						if (this.shouldCloseOnEscapePress) this.onCancelClicked();
					},
					...!this.neverRememberFocus && { lastFocused: document.activeElement },
				});
				this.trapFocus = true;
				this.$refs.focusBoundary.addEventListener('transitionend', this.onTransitionEnd);

				this.$emit('shown');
				this.opening = false;
			});
		},

		onConfirmClicked() {
			if (!this.disableCloseOnConfirm)
				this.onClose({ ok: true });
			/**
			 * Fired immediately when confirm button clicked.
			 * @event ok
			 * @type object
			 */
			this.$emit('ok');
		},
		onCancelClicked() {
			if (this.shouldCloseOnCancel)
				this.onClose({ cancel: true });
			/**
			 * Fired immediately when cancel button clicked.
			 * @event cancel
			 * @type object
			 */
			this.$emit('cancel');
		},

		async onClose(event) {
			if (this.$listeners && this.$listeners.closing) {
				let aborting = await new Promise((resolve) => {
					this.$emit('closing', (shouldAbort) => resolve(shouldAbort === undefined ? true : Boolean(shouldAbort)));
				});
				if (aborting)
					return;
			}

			this.closing = true;
			let stackItem = await this.stack.remove(this.id);

			this.showingContainer = false;
			this.trapFocus = false;
			if (this.$refs.focusBoundary) this.$refs.focusBoundary.removeEventListener('transitionend', this.onTransitionEnd);
			if (this.alwaysRememberFocus || (event && event.cancel)) this.restoreFocus(stackItem);
			await delay(150);
			this.showingMask = false;

			/**
			 * Fired immediately when dialog is to be closed.
			 * @event close
			 * @type object
			 */
			this.$emit('close', event);
			this.negotiateRemoveGlobalKeydownEventListener();
			this.$nextTick(() => {
				this.closing = false;
			});
			this.stack.popNextDialogIfWaiting();
		},
		onMaskClicked(e) {
			if (!this.shouldCloseOnOverlayClick) return;
			if (e.target !== this.$refs.mask) return;
			e.preventDefault();
			e.stopPropagation();
			this.onClose({ cancel: true });
		},
		handleEsc(e) {
			if (e.keyCode !== 27) return;

			e.preventDefault();
			e.stopPropagation();

			// handle esc click from top modal
			if (this.stack.length >= 1) {
				let modal = this.stack[this.stack.length - 1];
				modal.cancelWithEsc();
			}
		},
		negotiateAddGlobalKeydownEventListener() {
			if (this.stack.length === 0) {
				document.addEventListener('keydown', this.handleEsc);
			}
		},
		negotiateRemoveGlobalKeydownEventListener() {
			if (this.stack.length === 0) {
				document.removeEventListener('keydown', this.handleEsc);
			}
		},
		restoreFocus(stackItem) {
			if (!stackItem.lastFocused) return;
			if (this.focusNextOnClose) {
				window.focusNext({ target: stackItem.lastFocused });
			} else {
				stackItem.lastFocused.focus();
			}
		},

		onContainerShowing(el) {
			if (this.focusFirstOnShow) {
				this.$refs.focusBoundary.focusNext({ target: this.$refs.content });
				return;
			}
			if (this.disableContainerFocus) {
				return;
			}
			el.focus();
		},

		onTransitionEnd(event) {
			if (event.propertyName === 'background-color') {
				if (document.activeElement.matches('[data-focus-trap="true"], [data-focus-trap="true"] *')) {
					return; // If activeElement is the modal or a descendant do nothing.
				}
				this.onContainerShowing(this.$refs.focusBoundary);
			}
		},
	}
};
</script>


<style lang="scss" scoped>
@import '~@/components/evergreen/scss/mixins';
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.timpex-modal-mask {
	display: flex;
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 1100;
	overflow-y: auto;
	overflow-x: hidden;
	box-sizing: border-box;
	align-items: flex-start;
	-webkit-box-align: start;
	justify-content: center;
	-webkit-box-pack: center;

	&::before {
		background-color: get-color($background, 'overlay');
		left: 0px;
		top: 0px;
		position: fixed;
		display: block;
		width: 100%;
		height: 100%;
		content: " ";
	}
}

.timpex-modal-container {
	outline: none;
	z-index: 1200;
	display: flex;
	box-sizing: border-box;
	flex-direction: column;
	-webkit-box-orient: vertical;
	-webkit-box-direction: normal;
	margin-top: auto;
	margin-bottom: auto;
	margin-right: 16px;
	margin-left: 16px;
	max-height: calc(100% - 10vmin);
	max-width: calc(100% - 32px);
	min-width: 480px;
	border-radius: 5px;
	background-color: white;
	box-shadow: 0 0 1px 0 get-color($neutral-scale, 'N6A');
	align-self: center;
	overflow: hidden;

	/* Dummy transition used to detect attempt to escape focus trap */
	/* Stolen from: https://css-tricks.com/a-css-approach-to-trap-focus-inside-of-an-element */
	&[data-focus-trap="true"]:not(:focus-within) {
		background-color: rgb(255, 255, 254);
		transition: background-color 1ms ease;
	}
}

.timpex-modal-container.large {
	height: calc(100% - 3vmin);
	max-height: calc(100% - 3vmin);
	max-width: calc(100%);
}

.timpex-modal-header {
	flex-shrink: 0;
	padding: 16px;
	box-shadow: inset 0 -1px 0 0 get-color($neutral-scale, 'N4');
	align-items: center;
	-webkit-box-align: center;
	display: flex;
	box-sizing: border-box;
}

.timpex-modal-header-inner {
	flex: 1 1 0%;
	display: flex;
	flex-direction: row;
	align-items: center;
}

.timpex-modal-icon {
	margin-right: 10px;
}

.timpex-modal-title {
	@extend %font-families-ui;
	@extend %text-large;
	color: get-color($neutral-scale, 'N10');
	font-weight: 500;
	margin-bottom: 0px;
	margin-top: 0px;
	box-sizing: border-box;
}


.timpex-modal-body {
	@extend %font-families-ui;
	color: get-color($neutral-scale, 'N9');
	font-size: 14px;
	line-height: 1.5;
	letter-spacing: -0.1px;
	min-height: 80px;
	overflow: auto;
	padding: 16px;
	display: flex;
	box-sizing: border-box;
	flex-direction: column;
	-webkit-box-orient: vertical;
	-webkit-box-direction: normal;
	// Makes scroll bars much easier to see on modals. Both sides for balance.
	border-left: 1px solid white;
	border-right: 1px solid white;
	flex: 1;
}
.timpex-modal-body-scroll {
	max-height: 100%;
	overflow-y: auto;
	overflow-x: hidden;
}
.timpex-body-no-header {
	min-height:70px;
	margin-top:10px;
}
.timpex-body-no-header-scroll{
	margin-top:10px;
}

.timpex-modal-footer {
	flex: 0;
	box-shadow: inset 0 1px 0 0 get-color($neutral-scale, 'N4');
	box-sizing: border-box;
	&::before {
		content: ' ';
		display: table;
		clear: both;
	}
	&::after {
		content: ' ';
		display: table;
		clear: both;
	}
}

.timpex-modal-footer-content {
	display: flex;
	justify-content: space-between;
	padding: 16px;
	box-sizing: border-box;
}

.timpex-modal-button {
	margin-left: 12px;
	display: inline-flex;
}

.fade-enter, .fade-leave-to, .open-enter, .open-leave-to {
	opacity: 0;
}
@-webkit-keyframes fadeInAnimation {from{opacity:0;}to{opacity:1;}}
@-moz-keyframes fadeInAnimation {from{opacity:0;}to{opacity:1;}}
@-o-keyframes fadeInAnimation {from{opacity:0;}to{opacity:1;}}
@keyframes fadeInAnimation {from{opacity:0;}to{opacity:1;}}
.fade-enter-active, .fade-enter-to {
	animation: fadeInAnimation 240ms cubic-bezier(0, 0, 0.2, 1) 0s 1 normal both running;
}

@-webkit-keyframes fadeOutAnimation {from{opacity:1;}to{opacity:0;}}
@-moz-keyframes fadeOutAnimation {from{opacity:1;}to{opacity:0;}}
@-o-keyframes fadeOutAnimation {from{opacity:1;}to{opacity:0;}}
@keyframes fadeOutAnimation {from{opacity:1;}to{opacity:0;}}

.fade-leave-active, .fade-leave {
	animation: fadeOutAnimation 240ms cubic-bezier(0.4, 0, 1, 1) 0s 1 normal both running;
}

@-webkit-keyframes openAnimation{from{transform:scale(0.8);opacity:0;-webkit-transform:scale(0.8);}to{transform:scale(1);opacity:1;-webkit-transform:scale(1);}}
@-moz-keyframes openAnimation{from{transform:scale(0.8);opacity:0;-webkit-transform:scale(0.8);}to{transform:scale(1);opacity:1;-webkit-transform:scale(1);}}
@-o-keyframes openAnimation{from{transform:scale(0.8);opacity:0;-webkit-transform:scale(0.8);}to{transform:scale(1);opacity:1;-webkit-transform:scale(1);}}
@keyframes openAnimation{from{transform:scale(0.8);opacity:0;-webkit-transform:scale(0.8);}to{transform:scale(1);opacity:1;-webkit-transform:scale(1);}}
.open-enter-active, .open-enter-to {
	animation: openAnimation 200ms cubic-bezier(0, 0, 0.2, 1) 0s 1 normal both running;
}

@-webkit-keyframes closeAnimation{from{transform:scale(1);opacity:1;-webkit-transform:scale(1);}to{transform:scale(0.8);opacity:0;-webkit-transform:scale(0.8);}}
@-moz-keyframes closeAnimation{from{transform:scale(1);opacity:1;-webkit-transform:scale(1);}to{transform:scale(0.8);opacity:0;-webkit-transform:scale(0.8);}}
@-o-keyframes closeAnimation{from{transform:scale(1);opacity:1;-webkit-transform:scale(1);}to{transform:scale(0.8);opacity:0;-webkit-transform:scale(0.8);}}
@keyframes closeAnimation{from{transform:scale(1);opacity:1;-webkit-transform:scale(1);}to{transform:scale(0.8);opacity:0;-webkit-transform:scale(0.8);}}
.open-leave-active, .open-leave {
	animation: closeAnimation 200ms cubic-bezier(0.4, 0, 1, 1) 0s 1 normal both running;
}
</style>



<docs>
## Examples
```html
<t-dialog
	:isShown="visible"
	title="title"
	confirmLabel="confirmLabel"
	cancelLabel="cancelLabel"
	@close="visible=!visible"
	>Content</t-dialog>
```
```jsx noeditor
new Vue({
	data(){
		return {
			visible: false
		}
	},
	template: `
		<span>
			<div id="dialog-portal"/>
			<button @click="visible=!visible">Show Dialog</button>
			<t-dialog
				:isShown="visible"
				title="title"
				confirmLabel="confirmLabel"
				cancelLabel="cancelLabel"
				@close="visible=!visible"
				>Content</t-dialog>
		</span>
	`
});

```
</docs>
